import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { canAcceptPayments, paymentOrganizationKey, paymentProcessor, getStripeAccountId } from '../../../utils/paymentReadiness';
import { getAuthToken } from '../../../utils/auth';
import { accountsClient, isAccountsRouteConfigured } from '../../../lib/trpc/accounts';
import { APPROVAL_SYNC_GRACE_MS, getStripeSetupState } from '../stripeSetupState';

const OnboardingDriverContext = createContext(null);
export function useOnboardingDriver() { return useContext(OnboardingDriverContext); }

// Shared form UI state remains here while the retired tutorial no longer owns access.
export function OnboardingDriverProvider({ sidebarOpen = false, merchant = null, setMerchant = null, children }) {
  const orgKey = paymentOrganizationKey(merchant);
  const [createDropdownOpen, setCreateDropdownOpen] = useState(false);
  const [formSelectionModalOpen, setFormSelectionModalOpen] = useState(false);
  const [createDrawerOpen, setCreateDrawerOpen] = useState(false);
  const [publishedFormId, setPublishedFormId] = useState(null);
  const [formConfig, setFormConfig] = useState(null);
  const [liveStatus, setLiveStatus] = useState(null);
  const [paymentSetupOpen, updatePaymentSetupOpen] = useState(false);
  const [checkResult, setCheckResult] = useState(null);
  const [initialCheckSettledScope, setInitialCheckSettledScope] = useState(null);
  const [startedScope, setStartedScope] = useState(null);
  const [delayedScope, setDelayedScope] = useState(null);
  const [readyNoticeScope, setReadyNoticeScope] = useState(null);
  const previousPayment = useRef(null);
  const [refreshVersion, setRefreshVersion] = useState(0);
  const refreshPaymentStatus = useCallback(() => setRefreshVersion((v) => v + 1), []);
  const storedReady = canAcceptPayments(merchant);
  const isFinix = paymentProcessor(merchant) === 'FINIX';
  const accountId = getStripeAccountId(merchant);
  const accountScope = `${orgKey}:${accountId}`;
  const visit = useRef({ accountScope, generation: 0 });
  if (visit.current.accountScope !== accountScope) {
    visit.current = { accountScope, generation: visit.current.generation + 1 };
  }
  const scope = `${accountScope}:${visit.current.generation}`;
  // Setup intent persists across visits; live results belong to one visit only.
  const progressKey = `charitystack:stripe-setup-started:${accountScope}`;
  let rememberedStart = false;
  try { rememberedStart = sessionStorage.getItem(progressKey) === 'true'; } catch { /* Storage can be unavailable. */ }
  const setupStarted = startedScope === scope || rememberedStart;
  const markPaymentSetupStarted = useCallback(() => {
    setStartedScope(scope);
    try { sessionStorage.setItem(progressKey, 'true'); } catch { /* In-memory progress still works. */ }
  }, [scope, progressKey]);
  const setPaymentSetupOpen = useCallback((open) => {
    updatePaymentSetupOpen(open);
    if (!open) refreshPaymentStatus();
  }, [refreshPaymentStatus]);
  const currentLive = !merchant?.__devStateLabOverrides && liveStatus?.scope === scope ? liveStatus : null;
  const fixture = import.meta.env.DEV && merchant?.__devStateLabOverrides ? merchant.stripeStatusFixture : null;
  const paymentState = getStripeSetupState({
    merchant,
    liveStatus: fixture?.liveStatus || currentLive,
    setupStarted,
    checkFailed: fixture?.checkFailed || (checkResult?.scope === scope && !checkResult.success),
    syncDelayed: fixture?.syncDelayed || delayedScope === scope,
    initialCheckPending: Boolean(orgKey && accountId && !isFinix && !merchant?.__devStateLabOverrides && !currentLive && checkResult?.scope !== scope && import.meta.env.VITE_APP_MERCHANT_URL),
  });
  const { chargesEnabled, confirmationPending } = paymentState;

  useEffect(() => {
    const previous = previousPayment.current;
    previousPayment.current = { scope, ready: chargesEnabled, settled: initialCheckSettledScope === scope };
    if (previous?.scope === scope && previous.settled && !previous.ready && chargesEnabled) setReadyNoticeScope(scope);
  }, [scope, chargesEnabled, initialCheckSettledScope]);
  useEffect(() => {
    if (!readyNoticeScope) return;
    const timer = setTimeout(() => setReadyNoticeScope(null), 15_000);
    return () => clearTimeout(timer);
  }, [readyNoticeScope]);


  useEffect(() => {
    setDelayedScope(null);
    if (!confirmationPending) return;
    const timer = setTimeout(() => setDelayedScope(scope), APPROVAL_SYNC_GRACE_MS);
    return () => clearTimeout(timer);
  }, [confirmationPending, scope]);

  useEffect(() => {
    updatePaymentSetupOpen(false);
    setPublishedFormId(null);
    setFormConfig(null);
  }, [scope]);

  useEffect(() => {
    if (!orgKey || isFinix || merchant?.__devStateLabOverrides) return;
    if (!isAccountsRouteConfigured('GET /merchant')) return;
    let cancelled = false;
    let timer;
    let inFlight = false;
    let controller;
    const poll = async () => {
      if (inFlight || cancelled || document.visibilityState === 'hidden') return;
      inFlight = true;
      controller = new AbortController();
      const requestTimeout = setTimeout(() => controller.abort(), 8000);
      try {
        const tokenSignal = controller.signal;
        const token = await new Promise((resolve, reject) => {
          const abort = () => reject(new DOMException('Payment status check timed out', 'AbortError'));
          tokenSignal.addEventListener('abort', abort, { once: true });
          Promise.resolve().then(getAuthToken).then(resolve, reject).finally(() => tokenSignal.removeEventListener('abort', abort));
        });
        if (cancelled || controller.signal.aborted) return;
        // Apply each result immediately; neither endpoint may hold the other hostage.
        await Promise.allSettled([
          (async () => {
            try {
              const response = await accountsClient.request('GET /stripe/account-status', {
                authorization: token,
                input: { live: true },
                signal: controller.signal,
              });
              if (!response.ok) throw new Error(`Stripe status returned ${response.status}`);
              const status = await response.json();
              if (cancelled) return;
              const validStatus = status?.stripeApiAvailable === true && typeof status.chargesEnabled === 'boolean'
                && (!status.stripeAccountId || status.stripeAccountId === accountId);
              setCheckResult({ scope, success: validStatus });
              if (validStatus) setLiveStatus({
                scope,
                chargesEnabled: status.chargesEnabled,
                payoutsEnabled: typeof status.payoutsEnabled === 'boolean' ? status.payoutsEnabled : undefined,
                requirementsPastDue: Array.isArray(status.requirementsPastDue) ? status.requirementsPastDue : undefined,
                requirementsDisabledReason: status.requirementsDisabledReason,
                requirementsCurrentDeadline: status.requirementsCurrentDeadline,
                lastCheckedAt: status.lastCheckedAt || new Date().toISOString(),
                lastWebhookEventAt: status.lastWebhookEventAt,
                detailsSubmitted: typeof status.detailsSubmitted === 'boolean' ? status.detailsSubmitted : undefined,
                requirementsCurrentlyDue: Array.isArray(status.requirementsCurrentlyDue) ? status.requirementsCurrentlyDue : undefined,
                requirementsPendingVerification: Array.isArray(status.requirementsPendingVerification) ? status.requirementsPendingVerification : [],
              });
            } catch (error) {
              if (cancelled) return;
              setCheckResult({ scope, success: false });
              console.error('[payments] Stripe status refresh failed', error);
            }
          })(),
          (async () => {
            try {
              const response = await accountsClient.request('GET /merchant', {
                authorization: token,
                input: {},
                signal: controller.signal,
              });
              if (!response.ok) throw new Error(`Merchant refresh returned ${response.status}`);
              const fresh = await response.json();
              if (!cancelled && paymentOrganizationKey(fresh) === orgKey) setMerchant?.(fresh);
            } catch (error) {
              if (!cancelled) console.error('[payments] Merchant refresh failed', error);
            }
          })(),
        ]);
      } catch (error) {
        if (!cancelled) setCheckResult({ scope, success: false });
        console.error('[payments] Could not refresh account status', error);
      } finally {
        clearTimeout(requestTimeout);
        inFlight = false;
        if (!cancelled) {
          setInitialCheckSettledScope(scope);
          timer = setTimeout(poll, storedReady ? 60000 : 10000);
        }
      }
    };
    void poll();
    const onFocus = () => {
      if (document.visibilityState === 'hidden' || inFlight) return;
      clearTimeout(timer);
      void poll();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => { cancelled = true; clearTimeout(timer); controller?.abort(); window.removeEventListener('focus', onFocus); document.removeEventListener('visibilitychange', onFocus); };
  }, [orgKey, scope, accountId, isFinix, storedReady, refreshVersion, setMerchant, merchant?.__devStateLabOverrides]);

  const value = {
    isActivationMode: false, sidebarOpen: !!sidebarOpen, chargesEnabled,
    paymentSetupOpen, setPaymentSetupOpen, refreshPaymentStatus,
    ...paymentState,
    paymentStatus: paymentState.status,
    setupStarted, markPaymentSetupStarted,
    showPaymentReadyNotice: readyNoticeScope === scope,
    createDropdownOpen, setCreateDropdownOpen, formSelectionModalOpen, setFormSelectionModalOpen,
    createDrawerOpen, setCreateDrawerOpen, publishedFormId, setPublishedFormId, formConfig, setFormConfig,
    driverTransitioning: false, lockDriverTransition: () => {}, setMerchant,
  };
  return <OnboardingDriverContext.Provider value={value}>{children}</OnboardingDriverContext.Provider>;
}
